import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, X } from "lucide-react";
import { api, apiErr, inputCls, labelCls } from "./adminShared";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [newBlock, setNewBlock] = useState("");

  const load = () => api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return <p className="text-[#635B52]">Loading…</p>;

  const setHour = (day, field, val) => setS({ ...s, business_hours: { ...s.business_hours, [day]: { ...s.business_hours[day], [field]: val } } });
  const addBlocked = () => { if (newBlock && !s.blocked_dates.includes(newBlock)) { setS({ ...s, blocked_dates: [...s.blocked_dates, newBlock] }); setNewBlock(""); } };
  const removeBlocked = (d) => setS({ ...s, blocked_dates: s.blocked_dates.filter((x) => x !== d) });

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-end"><button onClick={save} className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-5 py-2.5 font-medium" data-testid="settings-save"><Save size={16} /> Save all settings</button></div>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] mb-3">Business hours (America/Chicago)</h3>
        <div className="space-y-2">
          {DAYS.map(([k, label]) => (
            <div key={k} className="flex items-center gap-3" data-testid={`hours-${k}`}>
              <span className="w-24 text-sm text-[#2C2621]">{label}</span>
              <label className="text-xs text-[#635B52] flex items-center gap-1"><input type="checkbox" checked={!s.business_hours[k].closed} onChange={(e) => setHour(k, "closed", !e.target.checked)} className="accent-[#B87333]" data-testid={`hours-open-${k}`} /> open</label>
              <input type="time" value={s.business_hours[k].open} onChange={(e) => setHour(k, "open", e.target.value)} className={`${inputCls} w-32`} disabled={s.business_hours[k].closed} />
              <span className="text-[#635B52]">to</span>
              <input type="time" value={s.business_hours[k].close} onChange={(e) => setHour(k, "close", e.target.value)} className={`${inputCls} w-32`} disabled={s.business_hours[k].closed} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5 grid sm:grid-cols-2 gap-4">
        <h3 className="font-serif text-xl text-[#2C2621] sm:col-span-2">Scheduling rules</h3>
        {[
          ["lead_time_hours", "Minimum advance booking (hours)"],
          ["travel_buffer_minutes", "Travel buffer between jobs (min)"],
          ["max_daily_appointments", "Max appointments per day"],
          ["cleaner_capacity", "Total cleaners on the crew"],
          ["crew_capacity", "Max simultaneous jobs (legacy)"],
          ["slot_interval_minutes", "Time slot interval (min)"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className={labelCls}>{label}</label>
            <input type="number" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} className={inputCls} data-testid={`setting-${k}`} />
          </div>
        ))}
        <label className="text-sm text-[#635B52] flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={s.email_notifications} onChange={(e) => setS({ ...s, email_notifications: e.target.checked })} className="accent-[#B87333]" data-testid="setting-email" /> Send email notifications to customers</label>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5 grid sm:grid-cols-2 gap-4">
        <h3 className="font-serif text-xl text-[#2C2621] sm:col-span-2">Texas Sales Tax</h3>
        <label className="text-sm text-[#635B52] flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={!!s.tax_enabled} onChange={(e) => setS({ ...s, tax_enabled: e.target.checked })} className="accent-[#B87333]" data-testid="setting-tax-enabled" /> Charge Texas Sales Tax on new bookings &amp; quotes</label>
        <div>
          <label className={labelCls}>Tax rate (%)</label>
          <input type="number" step="0.001" value={s.tax_rate != null ? Number((s.tax_rate * 100).toFixed(4)) : ""} onChange={(e) => setS({ ...s, tax_rate: (Number(e.target.value) || 0) / 100 })} className={inputCls} data-testid="setting-tax-rate" />
        </div>
        <div>
          <label className={labelCls}>Tax line label</label>
          <input value={s.tax_label ?? "Texas Sales Tax"} onChange={(e) => setS({ ...s, tax_label: e.target.value })} className={inputCls} data-testid="setting-tax-label" />
        </div>
        <p className="text-xs text-[#635B52] sm:col-span-2">Tax is calculated first; the 50% deposit is taken from the tax-inclusive total (any odd cent goes to the deposit). Existing bookings and accepted quotes keep their original agreed price and tax.</p>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] mb-3">Blocked dates</h3>
        <div className="flex gap-2">
          <input type="date" value={newBlock} onChange={(e) => setNewBlock(e.target.value)} className={`${inputCls} w-48`} data-testid="block-date-input" />
          <button onClick={addBlocked} className="inline-flex items-center gap-1 bg-[#2C2621] text-white rounded-full px-4 py-2 text-sm" data-testid="block-date-add"><Plus size={14} /> Block</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="blocked-dates">
          {s.blocked_dates.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 bg-[#F5F0E8] rounded-full px-3 py-1.5 text-sm text-[#2C2621]">{d} <button onClick={() => removeBlocked(d)}><X size={13} /></button></span>
          ))}
          {!s.blocked_dates.length && <span className="text-sm text-[#635B52]">None</span>}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5 grid sm:grid-cols-2 gap-4">
        <h3 className="font-serif text-xl text-[#2C2621] sm:col-span-2">Business Profile — Social media</h3>
        <p className="text-xs text-[#635B52] sm:col-span-2">Paste your full page URLs. Empty fields hide the icon on the website and email footers.</p>
        <div>
          <label className={labelCls}>Facebook page URL</label>
          <input value={s.facebook_url ?? ""} onChange={(e) => setS({ ...s, facebook_url: e.target.value })} className={inputCls} placeholder="https://facebook.com/…" data-testid="setting-facebook" />
        </div>
        <div>
          <label className={labelCls}>Instagram profile URL</label>
          <input value={s.instagram_url ?? ""} onChange={(e) => setS({ ...s, instagram_url: e.target.value })} className={inputCls} placeholder="https://instagram.com/…" data-testid="setting-instagram" />
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] mb-1">Local service area (ZIP codes)</h3>
        <p className="text-sm text-[#635B52] mb-3">Visitors who enter one of these ZIPs in the website inquiry popup see the WELCOME10 offer and a Book Now button. Others are saved as future-service-area leads. One ZIP per line or comma-separated.</p>
        <textarea rows={5} value={(s.service_area_zips || []).join(", ")} onChange={(e) => setS({ ...s, service_area_zips: e.target.value.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean) })} className={inputCls} data-testid="setting-service-zips" />
        <p className="mt-2 text-xs text-[#635B52]">{(s.service_area_zips || []).length} ZIP codes in service area.</p>
      </section>

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5 space-y-4">
        <h3 className="font-serif text-xl text-[#2C2621]">Policies</h3>
        {[["cancellation_policy", "Cancellation policy"], ["terms", "Terms & conditions"], ["privacy", "Privacy policy"]].map(([k, label]) => (
          <div key={k}><label className={labelCls}>{label}</label>
            <textarea rows={3} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={inputCls} data-testid={`policy-${k}`} /></div>
        ))}
      </section>
    </div>
  );
}
